import React, { useEffect, useState } from "react";
import {
    Box, Button, Paper, Tooltip, Typography, useMediaQuery,
    Stack, IconButton, CircularProgress, Divider, Grid, CssBaseline
} from "@mui/material";
import { useTheme } from "@emotion/react";
import RefreshIcon from '@mui/icons-material/Refresh';
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SchoolIcon from '@mui/icons-material/School';
import EscalatorWarningIcon from '@mui/icons-material/EscalatorWarning';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Header from "../../component/header/Header";

// Standardized UI Colors
const colors = {
    pageBg: '#F8F9FA',
    sidebarBg: '#F1F2F6',
    accent: '#FFC107',
    accentDark: '#FFA000',
    textMain: '#5D4037',
    white: '#FFFFFF',
    filterBg: "#FFF9E6",
    border: '#F0E6CC'
};

// Reusable Circular Gauge Component
const CircularGauge = ({ value, total, label, color }) => {
    const percentage = total > 0 ? Math.round((value / total) * 100) : 0;

    return (
        <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            {/* Background Circle */}
            <CircularProgress
                variant="determinate"
                value={100}
                size={80}
                thickness={4}
                sx={{ color: '#EEEEEE' }}
            />
            {/* Actual Progress */}
            <CircularProgress
                variant="determinate"
                value={percentage}
                size={80}
                thickness={4}
                sx={{
                    color: color,
                    position: 'absolute',
                    left: 0,
                    strokeLinecap: 'round'
                }}
            />
            <Box sx={{ position: 'absolute', textAlign: 'center' }}>
                <Typography variant="caption" fontWeight="bold" component="div" color="text.primary">
                    {percentage}%
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '8px', textTransform: 'uppercase' }}>
                    {label}
                </Typography>
            </Box>
        </Box>
    );
};

const DashboardScreen = () => {
    const isNonMobile = useMediaQuery("(min-width:1100px)");
    const navigation = useNavigate();

    const [loading, setLoading] = useState(true);
    const [totalNumbers, setTotalNumbers] = useState({
        tutor: 0, admin: 0, staff: 0, student: 0, parent: 0, paid: 0, submitted: 0
    });

    useEffect(() => {
        getTotalNumbers();
        localStorage.setItem("current_tab", "Dashboard");
    }, []);

    const getTotalNumbers = async () => {
        setLoading(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/dashboard`);
            const resJson = await response.json();
            if (resJson.status === 1) setTotalNumbers(resJson.data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
            <CssBaseline />
            <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "white", m: 2, borderRadius: 5 }}>
                {/* Header Area */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                    <Header title="DASHBOARD" subtitle="Genius Milenia Management Portal" />
                    <Button
                        variant="contained"
                        startIcon={<RefreshIcon />}
                        onClick={getTotalNumbers}
                        sx={{ bgcolor: colors.accent, color: 'black', borderRadius: 3, px: 3, '&:hover': { bgcolor: colors.accentDark } }}
                    >
                        Refresh
                    </Button>
                </Stack>

                <Grid container spacing={3}>

                    {/* STAFF VISUALIZATION CARD */}
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} sx={{ p: 3, borderRadius: 5, border: `1px solid ${colors.border}`, bgcolor: colors.white, height: '100%' }}>
                            <Stack direction="row" justifyContent="space-between" mb={2}>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold">Total Staff</Typography>
                                    <Typography variant="h3" fontWeight="900" sx={{ color: colors.textMain }}>
                                        {loading ? "..." : totalNumbers.staff}
                                    </Typography>
                                </Box>
                                <SchoolIcon sx={{ color: colors.accent, fontSize: 40 }} />
                            </Stack>

                            <Divider sx={{ my: 2 }} />

                            <Stack direction="row" justifyContent="space-around" alignItems="center">
                                <CircularGauge value={totalNumbers.tutor} total={totalNumbers.staff} label="Tutors" color={colors.accentDark} />
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Admins: <b>{totalNumbers.admin}</b></Typography>
                                    <Typography variant="body2" color="text.secondary">Tutors: <b>{totalNumbers.tutor}</b></Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Grid>

                    {/* PARENT / PAYMENT VISUALIZATION CARD */}
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} sx={{ p: 3, borderRadius: 5, border: `1px solid ${colors.border}`, bgcolor: colors.white, height: '100%' }}>
                            <Stack direction="row" justifyContent="space-between" mb={2}>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold">Total Families</Typography>
                                    <Typography variant="h3" fontWeight="900" sx={{ color: colors.textMain }}>
                                        {loading ? "..." : totalNumbers.parent}
                                    </Typography>
                                </Box>
                                <Groups2OutlinedIcon sx={{ color: colors.accent, fontSize: 40 }} />
                            </Stack>

                            <Divider sx={{ my: 2 }} />

                            <Stack direction="row" justifyContent="space-around" alignItems="center">
                                <CircularGauge value={totalNumbers.paid} total={totalNumbers.parent} label="Paid" color="#2E7D32" />
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Paid: <b>{totalNumbers.paid}</b></Typography>
                                    <Typography variant="body2" color="error">Unpaid: <b>{totalNumbers.parent - totalNumbers.paid}</b></Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Grid>

                    {/* STUDENT QUICK STAT CARD */}
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} sx={{ p: 3, borderRadius: 5, bgcolor: colors.pageBg, color: 'black', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', border: `1px solid ${colors.border}` }}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Box sx={{ bgcolor: 'rgba(0,0,0,0.1)', p: 2, borderRadius: 4 }}>
                                    <EscalatorWarningIcon sx={{ fontSize: 40 }} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold">Active Students</Typography>
                                    <Typography variant="h2" fontWeight="900">{loading ? "..." : totalNumbers.student}</Typography>
                                </Box>
                            </Stack>
                            <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
                                Avg Students per Parent: {totalNumbers.parent > 0 ? (totalNumbers.student / totalNumbers.parent).toFixed(1) : 0}
                            </Typography>
                        </Paper>
                    </Grid>

                    {/* ACTION BUTTONS GRID */}
                    <Grid item xs={12}>
                        <Typography variant="h6" fontWeight="bold" mb={2}>Quick Actions</Typography>
                        <Grid container spacing={2}>
                            {[
                                { label: "Add Staff", icon: <AddCircleOutlineIcon />, path: "/Portal/Staff/Register", color: colors.white },
                                { label: "Add Parent", icon: <GroupAddIcon />, path: "/Portal/Parent/Register", color: colors.white },
                                { label: "Manual Receipt", icon: <DescriptionOutlinedIcon />, path: "/Portal", color: colors.filterBg },
                                { label: "Manual Invoice", icon: <DescriptionOutlinedIcon />, path: "/Portal", color: colors.filterBg },
                            ].map((btn, i) => (
                                <Grid item xs={6} sm={3} key={i}>
                                    <Button
                                        fullWidth
                                        onClick={() => navigation(btn.path)}
                                        variant="outlined"
                                        startIcon={btn.icon}
                                        sx={{
                                            py: 2, borderRadius: 4, bgcolor: btn.color, color: 'black',
                                            borderColor: colors.border, fontWeight: 'bold', textTransform: 'none',
                                            '&:hover': { bgcolor: colors.accent, borderColor: colors.accent }
                                        }}
                                    >
                                        {btn.label}
                                    </Button>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>

                    {/* Export & Detailed Metrics */}
                    <Grid item xs={12} md={8}>
                        <Paper elevation={0} sx={{ p: 3, borderRadius: 5, border: `1px solid ${colors.border}`, bgcolor: colors.white }}>
                            <Typography variant="h6" fontWeight="bold" mb={3}>Data Export Center</Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <Button
                                    fullWidth variant="outlined" startIcon={<CloudDownloadIcon />}
                                    onClick={() => { }} // getStudentList logic here
                                    sx={{ py: 2, borderRadius: 4, border: `2px solid ${colors.border}`, color: colors.textMain, fontWeight: 'bold' }}
                                >
                                    Download Student CSV
                                </Button>
                                <Button
                                    fullWidth variant="outlined" startIcon={<CloudDownloadIcon />}
                                    onClick={() => { }} // getParentList logic here
                                    sx={{ py: 2, borderRadius: 4, border: `2px solid ${colors.border}`, color: colors.textMain, fontWeight: 'bold' }}
                                >
                                    Download Parent CSV
                                </Button>
                            </Stack>
                        </Paper>
                    </Grid>

                    {/* INVOICING SUMMARY */}
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} sx={{ p: 3, borderRadius: 5, border: `1px solid ${colors.border}`, bgcolor: colors.filterBg }}>
                            <Typography variant="h6" fontWeight="bold">Invoicing Status</Typography>
                            <Stack direction="row" spacing={3} mt={2} alignItems="center">
                                <CircularGauge value={totalNumbers.submitted} total={totalNumbers.parent} label="Sent" color={colors.accentDark} />
                                <Box>
                                    <Typography variant="h4" fontWeight="bold">{totalNumbers.submitted}</Typography>
                                    <Typography variant="caption" display="block">INVOICES SUBMITTED</Typography>
                                    <Typography variant="caption" color="error">Pending: {totalNumbers.parent - totalNumbers.submitted}</Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export default DashboardScreen;